import { ThemeEvents, VariantUpdateEvent } from "@theme/events";
import { formatCurrency } from "@theme/utilities";

/**
 * The cents a campaign takes off an amount. Shopify rounds a percentage discount down to the cent,
 * and so does this, matching `snippets/price.liquid`; the tiny addend keeps a float product that
 * should be a whole cent from landing just below it.
 *
 * @param {number} amount - In cents.
 * @param {number} percent
 * @returns {number}
 */
const campaignReduction = (amount, percent) => Math.floor((amount * percent) / 100 + 1e-6);

/** Size of each unit in the base unit of its kind; only units of one kind convert into each other. */
const UNIT_SCALES = {
  mg: ["mass", 0.001],
  g: ["mass", 1],
  kg: ["mass", 1000],
  ml: ["volume", 1],
  cl: ["volume", 10],
  l: ["volume", 1000],
  m3: ["volume", 1000000],
  mm: ["length", 1],
  cm: ["length", 10],
  m: ["length", 1000],
};

/**
 * The unit price for a campaign-reduced price: the reduced price divided by the measured quantity,
 * computed in the same order as `snippets/price.liquid` so both land on the same cent. Scaling
 * Shopify's already-rounded unit price can land a cent off. Units that cannot be converted fall back
 * to scaling.
 *
 * @param {number | null} unitPrice - Shopify's unit price for the unreduced price, in cents.
 * @param {number} reducedPrice
 * @param {number} originalPrice
 * @param {{quantity_value?: number | string, quantity_unit?: string, reference_value: number, reference_unit: string} | null | undefined} measurement
 * @returns {number | null}
 */
const campaignUnitPrice = (unitPrice, reducedPrice, originalPrice, measurement) => {
  if (!unitPrice) return unitPrice;

  const quantityValue = Number(measurement?.quantity_value);
  const referenceValue = Number(measurement?.reference_value);
  let scales = null;

  if (measurement?.quantity_unit && measurement.quantity_unit === measurement.reference_unit) {
    scales = [1, 1];
  } else {
    const quantityUnit = UNIT_SCALES[measurement?.quantity_unit ?? ""];
    const referenceUnit = UNIT_SCALES[measurement?.reference_unit ?? ""];
    if (quantityUnit && referenceUnit && quantityUnit[0] === referenceUnit[0]) {
      scales = [quantityUnit[1], referenceUnit[1]];
    }
  }

  if (scales && quantityValue > 0 && referenceValue > 0) {
    return Math.round((reducedPrice * (referenceValue * scales[1])) / (quantityValue * scales[0]) + 1e-6);
  }

  return originalPrice > 0 ? Math.round((unitPrice * reducedPrice) / originalPrice) : unitPrice;
};

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  #currentVariantId = null;
  #section = null;
  /** The one-time markup a subscription render replaced, so switching back restores it exactly. */
  #oneTimePrice = null;
  #sellingPlanObserver = null;
  /**
   * The variant the selling plan is priced for. Kept apart from #currentVariantId, which also takes
   * the ids of product cards' swatch events bubbling through the same section.
   */
  #sellingPlanVariantId = null;

  connectedCallback() {
    const closestSection = this.closest(".shopify-section, dialog");
    const closestCard = this.closest("product-card");

    if (!closestSection || closestCard) return;

    this.#section = closestSection;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    // Registered after updatePrice so it runs once any server HTML is already in place.
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.#reapplySellingPlan);
    this.addEventListener("price:update-optimistic", this.updatePriceOptimistic);

    // The subscription app sets the form's selling plan asynchronously — after its own change event,
    // and on load when it preselects a plan — so the input is watched rather than the widget's events.
    // Watched even without a selling plan map, which a product switch can bring in later.
    this.#sellingPlanObserver = new MutationObserver(this.#onSellingPlanMutation);
    this.#sellingPlanObserver.observe(closestSection, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["value"],
    });
    this.#applySellingPlan(this.#formVariantId());

    const source = this.#sellingPlanSource();
    if (source) this.#syncWidgetPrices(source);
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#reapplySellingPlan);
    this.#sellingPlanObserver?.disconnect();
    this.#sellingPlanObserver = null;
    this.#section = null;
    this.removeEventListener("price:update-optimistic", this.updatePriceOptimistic);
  }

  /**
   * The selling plan chosen in the subscription widget, or "" for a one-time purchase.
   *
   * Read from the theme's own add-to-cart form, which the subscription app fills in because that is
   * what reaches the cart, rather than from the widget's markup.
   *
   * @returns {string}
   */
  #selectedSellingPlanId() {
    const input = this.#section?.querySelector('form[data-type="add-to-cart-form"] input[name="selling_plan"]');
    return input instanceof HTMLInputElement ? input.value : "";
  }

  #formVariantId() {
    const input = this.#section?.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
    return input instanceof HTMLInputElement ? input.value : null;
  }

  /**
   * The price element carrying this product's selling plan data: this one, or the product's price
   * block when this is a second price in the same section, such as the sticky add-to-cart bar.
   *
   * @returns {HTMLElement | null}
   */
  #sellingPlanSource() {
    const selector = "[data-selling-plan-map]";
    const map = this.querySelector(selector) ?? this.#section?.querySelector(selector);
    const source = map?.closest("product-price");
    if (!(source instanceof HTMLElement)) return null;

    return source === this || source.dataset.productId === this.dataset.productId ? source : null;
  }

  /**
   * @param {HTMLElement} source
   * @returns {Record<string, {price: number, unit_price_measurement: {quantity_value: number, quantity_unit: string, reference_value: number, reference_unit: string} | null, plans: Record<string, {price: number, unit_price: number | null}>}> | null}
   */
  #sellingPlanMap(source) {
    const map = source.querySelector("[data-selling-plan-map]");
    if (!map) return null;

    try {
      return JSON.parse(map.textContent);
    } catch (error) {
      console.warn("Unreadable selling plan map", error);
      return null;
    }
  }

  /**
   * @param {MutationRecord[]} records
   */
  #onSellingPlanMutation = (records) => {
    const source = this.#sellingPlanSource();
    if (!source) return;

    const form = 'form[data-type="add-to-cart-form"]';
    const isSellingPlanInput = (node) => node instanceof HTMLInputElement && node.name === "selling_plan";

    // A removed input is already detached, so its form is found from the record's target instead.
    const touchesSellingPlan = records.some((record) => {
      if (record.type === "attributes") {
        return isSellingPlanInput(record.target) && Boolean(record.target.closest(form));
      }

      const insideForm = record.target instanceof Element && Boolean(record.target.closest(form));
      return [...record.addedNodes, ...record.removedNodes].some(
        (node) =>
          (insideForm && isSellingPlanInput(node)) ||
          (node instanceof Element && Boolean(node.querySelector('input[name="selling_plan"]')))
      );
    });

    if (touchesSellingPlan) this.#applySellingPlan(this.#sellingPlanVariantId ?? this.#formVariantId(), source);

    // The widget re-renders its own prices on variant and plan changes, and the form's variant id can
    // change after it has done so.
    const widget = ".sls-purchase-options-container";
    const touchesWidget = records.some(
      (record) =>
        (record.target instanceof Element && Boolean(record.target.closest(widget))) ||
        (record.type === "attributes" &&
          record.target instanceof HTMLInputElement &&
          record.target.name === "id" &&
          Boolean(record.target.closest(form))) ||
        [...record.addedNodes].some(
          (node) => node instanceof Element && (node.matches(widget) || Boolean(node.querySelector(widget)))
        )
    );

    if (touchesWidget) this.#syncWidgetPrices(source);
  };

  /**
   * Writes the campaign price into the subscription widget's own price labels.
   *
   * The widget prices from its own copy of the catalogue and knows nothing of campaigns, so without
   * this it quotes more than the headline price and the cart. It uses the headline's rules: the
   * one-time row always takes a running campaign, the subscription row only when campaigns reach
   * subscriptions. Only a differing label is written, so the mutation this write itself causes
   * settles after one pass.
   *
   * @param {HTMLElement} source
   */
  #syncWidgetPrices(source) {
    if (source !== this) return;

    const widget = this.#section?.querySelector(".sls-purchase-options-container");
    const variantId = this.#formVariantId();
    const entry = widget ? this.#sellingPlanMap(source)?.[String(variantId)] : undefined;
    const campaign = entry ? this.#campaignFor(variantId) : null;
    if (!widget || !entry || !campaign) return;

    // Only the number is replaced, keeping the widget's own money format, which differs from the theme's.
    const writeLabel = (container, amount) => {
      const label = container?.querySelector(".sls-price .money");
      if (!label) return;

      const separator = /\d([.,])\d{2}(?!\d)/.exec(label.textContent)?.[1] ?? ".";
      const text = label.textContent.replace(/\d[\d.,\s ]*\d/, (amount / 100).toFixed(2).replace(".", separator));
      if (text !== label.textContent) label.textContent = text;
    };

    writeLabel(widget.querySelector(".sls-one-time-price"), entry.price - campaignReduction(entry.price, campaign.percent));

    if (this.dataset.subscriptionCampaign !== "true") return;

    for (const groupPrice of widget.querySelectorAll(".sls-selling-plan-group-price")) {
      const interval = groupPrice.closest(".sls-option-container")?.querySelector('select[name="subs_interval"]');
      const allocation = interval instanceof HTMLSelectElement ? entry.plans[interval.value] : undefined;
      if (allocation) writeLabel(groupPrice, allocation.price - campaignReduction(allocation.price, campaign.percent));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #reapplySellingPlan = (event) => {
    if (event.detail.data.isOptimistic) return;

    // Product cards in the same section announce their own variants through this event.
    const fromCard = event.target instanceof Element && Boolean(event.target.closest("product-card"));
    const productId = event.detail.data.productId;
    if (fromCard || (productId && String(productId) !== String(this.dataset.productId))) return;

    // The product form takes its variant id from this same event, so pricing the plan for it keeps
    // the page in step with what the cart will receive.
    this.#sellingPlanVariantId = event.detail.resource?.id ?? this.#formVariantId();

    // Deferred until every listener of this event has run: the sticky add-to-cart bar re-renders
    // from the same event after this one and would overwrite a plan price applied now.
    queueMicrotask(() => this.#applySellingPlan(this.#sellingPlanVariantId));
  };

  /**
   * Shows the selected selling plan's price for a variant, or the one-time price when no plan is
   * selected or the variant does not offer the selected plan.
   *
   * The plan price is Shopify's own allocation, which is what the cart charges for the first
   * delivery. A campaign is taken off it only when the theme setting says campaigns reach
   * subscriptions, because the theme cannot read the discount's purchase type.
   *
   * @param {number|string|null} variantId
   * @param {HTMLElement | null} [knownSource] - The selling plan source when the caller already resolved it.
   */
  #applySellingPlan(variantId, knownSource) {
    const priceContainer = this.querySelector('[ref="priceContainer"]');
    if (!(priceContainer instanceof HTMLElement) || variantId == null) return;
    if (priceContainer.dataset.quantityBreaks === "true") return;

    const source = knownSource ?? this.#sellingPlanSource();
    const map = source ? this.#sellingPlanMap(source) : null;
    if (!map) return;

    // A container without the marker was rendered afresh for the current variant, so the saved
    // one-time markup belongs to a previous render and must not be restored over it.
    if (!priceContainer.dataset.sellingPlan) this.#oneTimePrice = null;

    const entry = map[String(variantId)];
    const planId = this.#selectedSellingPlanId();
    const allocation = planId ? entry?.plans?.[planId] : undefined;

    if (!entry || !allocation) {
      if (this.#oneTimePrice) {
        priceContainer.innerHTML = this.#oneTimePrice.html;
        priceContainer.className = this.#oneTimePrice.className;
        delete priceContainer.dataset.sellingPlan;
        this.#oneTimePrice = null;
      }
      return;
    }

    if (!this.#oneTimePrice) {
      this.#oneTimePrice = { html: priceContainer.innerHTML, className: priceContainer.className };
    }

    let priceAmount = allocation.price;
    let unitPriceAmount = allocation.unit_price;
    const campaign = this.#campaignFor(variantId, source);
    const subscriptionCampaign = source.dataset.subscriptionCampaign === "true" ? campaign : null;

    if (subscriptionCampaign) {
      priceAmount -= campaignReduction(priceAmount, subscriptionCampaign.percent);
      unitPriceAmount = campaignUnitPrice(unitPriceAmount, priceAmount, allocation.price, entry.unit_price_measurement);
    }

    // The strikethrough is the one-time price the shopper would pay instead, so it takes the campaign
    // the one-time price always shows.
    const oneTimeAmount = campaign ? entry.price - campaignReduction(entry.price, campaign.percent) : entry.price;

    // A price that borrows another element's data keeps its own layout: it gains no unit price it
    // was not rendered with.
    const unitPriceMeasurement =
      source === this || priceContainer.querySelector(".unit-price") ? entry.unit_price_measurement : null;

    priceContainer.dataset.sellingPlan = planId;
    this.#writePrices(priceContainer, priceAmount, oneTimeAmount, unitPriceAmount, unitPriceMeasurement);
  }

  /**
   * The promotional campaign covering a variant, or null.
   *
   * Read fresh rather than cached: a combined-listing switch puts a different product's map in place,
   * and a cached one would price the new variants against the old product's campaigns. A miss always
   * means "no campaign", which shows the plain price — the same direction the server takes when it
   * cannot resolve one.
   *
   * @param {number|string} variantId
   * @param {HTMLElement} [scope] - The price element whose campaign map is read.
   * @returns {{percent: number} | null}
   */
  #campaignFor(variantId, scope = this) {
    const source = scope.querySelector("[data-campaign-map]");
    if (!source || variantId == null) return null;

    try {
      const campaign = JSON.parse(source.textContent)[String(variantId)];
      return typeof campaign?.percent === "number" && campaign.percent > 0 && campaign.percent <= 100
        ? campaign
        : null;
    } catch (error) {
      console.warn("Unreadable campaign map", error);
      return null;
    }
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    const incomingVariantId = event.detail.resource?.id;
    const variant = event.detail.resource;

    // Skip if this is from cache - optimistic update already handled it
    if (event.detail.data.isOptimistic) {
      // Update tracking ID for cache hit
      this.#currentVariantId = incomingVariantId;
      return;
    }

    const isProductSwitch = !!event.detail.data.newProduct;

    // Skip background sync only for same-product updates (optimistic already handled it).
    // For combine listing switches (isProductSwitch), there is no optimistic path so we must update here.
    if (event.detail.data.isBackgroundSync && !isProductSwitch) {
      return;
    }

    // 🛡️ RACE CONDITION FIX: Check if this HTML is for current variant
    // Skip for product switches — different product means variant IDs are unrelated.
    if (!isProductSwitch && this.#currentVariantId && incomingVariantId && String(this.#currentVariantId) !== String(incomingVariantId)) {
      console.warn(
        `Skipping outdated price morph (current: ${this.#currentVariantId}, incoming: ${incomingVariantId})`
      );
      return;
    }

    // Update tracking ID
    this.#currentVariantId = incomingVariantId;

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Check if HTML exists (might be null from cache)
    if (!event.detail.data.html) {
      console.warn("No HTML in event data");
      return;
    }

    // On a product switch the incoming campaign map has to come with the price. Only the product page
    // morphs its whole main element; inside a quick add dialog nothing else replaces this map, and the
    // previous product's campaigns would keep answering for variants that are no longer on the page.
    if (isProductSwitch) {
      for (const selector of ["[data-campaign-map]", "[data-selling-plan-map]"]) {
        const incomingMap = event.detail.data.html.querySelector(selector);
        this.querySelector(selector)?.remove();
        if (incomingMap) this.appendChild(incomingMap);
      }
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    // Handle case when variant is null (no valid selection)
    // This happens when fallback HTML fetch is triggered without variantId
    if (!variant) {
      // For product switches: replace container so the new product's HTML structure
      // (incl. data-quantity-breaks) is in place for subsequent optimistic updates.
      if (isProductSwitch && currentPrice.innerHTML !== newPrice.innerHTML) {
        currentPrice.replaceWith(newPrice);
      }
      this.style.display = "none";
      return;
    }

    // Variant exists - show price and morph if needed
    this.style.display = "";

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }
  };

  /**
   * ⚡ Optimistic price update from cached variant data (instant, no network delay)
   *
   * This method updates the price UI immediately using variant data from cache/JSON,
   * without waiting for a full HTML fetch. This provides instant feedback to users.
   *
   * Updates:
   * - Toggle .price--on-sale class on container (CSS handles show/hide)
   * - Main price value in .price__sale and .price__regular
   * - Compare-at-price value in .price__sale
   *
   * Race condition protection: Updates #currentVariantId to prevent stale HTML from
   * overriding this optimistic update (see updatePrice method).
   *
   * @param {CustomEvent} event - The optimistic update event with variant data
   */
  updatePriceOptimistic = (event) => {
    const variant = event.detail.variant;

    // Find price container first
    const priceContainer = this.querySelector('[ref="priceContainer"]');
    if (!priceContainer) {
      console.warn("Price container not found");
      return;
    }

    // Hide price if variant doesn't exist (no valid selection)
    if (!variant) {
      this.style.display = "none";
      return;
    }

    // Track current variant ID to prevent race conditions
    // This ensures stale HTML responses won't override this optimistic update
    this.#currentVariantId = variant.id;

    try {
      // Restore visibility (variant exists; hide case is handled above)
      this.style.display = "";

      // If product has volume/quantity rules, price is a range (product-level).
      // Skip DOM price updates — background HTML handles the range correctly.
      if (priceContainer.dataset.quantityBreaks === "true") {
        return;
      }

      // A promotional campaign is applied here as well as server-side, because this path runs before
      // the server HTML arrives and would otherwise show the unreduced price for a moment. The
      // reduction is rounded, not the reduced price, matching both the Liquid and what Shopify takes
      // off at checkout. The pre-campaign price becomes the strikethrough unless a larger compare-at
      // price already exists.
      const campaign = this.#campaignFor(variant.id);
      let priceAmount = variant.price;
      let compareAtAmount = variant.compare_at_price;
      let unitPriceAmount = variant.unit_price;

      if (campaign) {
        compareAtAmount = compareAtAmount > priceAmount ? compareAtAmount : priceAmount;
        priceAmount -= campaignReduction(priceAmount, campaign.percent);
        unitPriceAmount = campaignUnitPrice(unitPriceAmount, priceAmount, variant.price, variant.unit_price_measurement);
      }

      this.#writePrices(priceContainer, priceAmount, compareAtAmount, unitPriceAmount, variant.unit_price_measurement);

      // The container now shows this variant's one-time price, which is what a subscription render
      // for it has to be able to restore.
      delete priceContainer.dataset.sellingPlan;
      this.#oneTimePrice = null;
      this.#sellingPlanVariantId = variant.id;
      this.#applySellingPlan(variant.id);
    } catch (error) {
      console.error("Error updating price:", error);
    }
  };

  /**
   * Writes amounts in cents into the server-rendered price markup.
   *
   * @param {HTMLElement} priceContainer
   * @param {number} priceAmount
   * @param {number | null} compareAtAmount
   * @param {number | null} unitPriceAmount
   * @param {{reference_value: number, reference_unit: string} | null | undefined} unitPriceMeasurement
   */
  #writePrices(priceContainer, priceAmount, compareAtAmount, unitPriceAmount, unitPriceMeasurement) {
    // Get money format from theme settings
    const moneyFormat = window.FoxTheme?.moneyFormat || "${{amount}}";

    // Format prices using theme's money format
    const price = formatCurrency(priceAmount, moneyFormat);
    const comparePrice = compareAtAmount > priceAmount ? formatCurrency(compareAtAmount, moneyFormat) : null;

    const isOnSale = Boolean(comparePrice);

    // Toggle class .price--on-sale to let CSS handle show/hide
    if (isOnSale) {
      priceContainer.classList.add("price--on-sale");
    } else {
      priceContainer.classList.remove("price--on-sale");
    }

    // Update .price__sale (only 1 div exists at a time)
    const salePriceDiv = priceContainer.querySelector(".price__sale");
    if (salePriceDiv) {
      const salePriceEl = salePriceDiv.querySelector(".price");
      const comparePriceEl = salePriceDiv.querySelector(".compare-at-price");

      if (salePriceEl) salePriceEl.textContent = price;
      if (comparePriceEl && comparePrice) {
        comparePriceEl.textContent = comparePrice;
      }
    }

    // Update .price__regular (only 1 div exists, but be careful with price range)
    const regularPriceDiv = priceContainer.querySelector(".price__regular");
    if (regularPriceDiv) {
      const regularPriceEl = regularPriceDiv.querySelector(".price");
      // Only update if not a price range (check for range indicators)
      if (
        regularPriceEl &&
        !regularPriceEl.textContent.includes("–") &&
        !regularPriceEl.textContent.includes("from")
      ) {
        regularPriceEl.textContent = price;
      }
    }

    // Update unit price
    const unitPriceEl = priceContainer.querySelector(".unit-price");

    if (unitPriceAmount && unitPriceMeasurement) {
      const unitPrice = formatCurrency(unitPriceAmount, moneyFormat);
      const { reference_value, reference_unit } = unitPriceMeasurement;
      const unitLabel = reference_value !== 1 ? `${reference_value}${reference_unit}` : reference_unit;
      const unitPriceText = ` ${unitPrice}/${unitLabel} `;

      if (unitPriceEl) {
        // Element exists — update the non-whitespace text node (skip leading whitespace nodes)
        const textNode = [...unitPriceEl.childNodes].find(
          (n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim() !== ""
        );
        if (textNode) {
          textNode.textContent = unitPriceText;
        } else {
          unitPriceEl.appendChild(document.createTextNode(unitPriceText));
        }
        unitPriceEl.style.display = "";
      } else {
        // Element doesn't exist yet — create and append (variant had no unit price initially)
        const small = document.createElement("small");
        small.className = "unit-price color-subtext";
        small.innerHTML = `<span class="visually-hidden">${window.FoxTheme?.translations?.unit_price || "Unit price"}</span>${unitPriceText}`;
        priceContainer.appendChild(small);
      }
    } else if (unitPriceEl) {
      unitPriceEl.style.display = "none";
    }
  }
}

if (!customElements.get("product-price")) {
  customElements.define("product-price", ProductPrice);
}
